import PersonIcon from '@mui/icons-material/Person';
import { AppBar, Box, Button, Toolbar, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAnalytics } from '../hooks/useAnalytics';
import { Container } from './atomic';
import { Logo, MobileDrawer, MobileMenuButton, NavigationButton } from './ui';

/**
 * Header component that provides navigation functionality for the EllaFin website.
 *
 * Features include:
 * - EllaFin logo with click-to-top functionality
 * - Responsive navigation with mobile drawer menu
 * - Simple navigation for Product, Pricing, About, Contact
 * - Mobile-friendly hamburger menu using Material-UI Drawer
 * - Clean, reusable component architecture
 *
 * @returns {JSX.Element} Header component with navigation
 */
function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:680px)');
  const isTablet = useMediaQuery('(min-width:681px) and (max-width:959px)');
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isOriginalTheme = theme.custom.id === 'original';
  const headerTextColor = isOriginalTheme
    ? theme.palette.primary.main
    : '#ffffff';
  const { trackEvent, trackCTA } = useAnalytics();

  /**
   * Handles logo click - navigates home or scrolls to top
   */
  const handleLogoClick = () => {
    trackEvent('logo_click', {
      location: 'header',
      action: isHomePage ? 'scroll_to_top' : 'navigate_home',
      from_page: location.pathname,
    });

    if (isHomePage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
    setMobileOpen(false);
  };

  /**
   * Navigation items configuration for EllaFin
   * Note: Pricing is intentionally excluded until pricing is finalized
   */
  const navigationItems = [
    {
      label: 'Product',
      href: '/product',
      onClick: () => {
        trackCTA('header_nav', 'Product', {
          destination: '/product',
          from_page: location.pathname,
        });
        navigate('/product');
        setMobileOpen(false);
      },
    },
    {
      label: 'About',
      href: '/about',
      onClick: () => {
        trackCTA('header_nav', 'About', {
          destination: '/about',
          from_page: location.pathname,
        });
        navigate('/about');
        setMobileOpen(false);
      },
    },
  ];

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        color="inherit"
        sx={{
          backgroundColor: theme.custom.headerBackground,
          color: headerTextColor,
          borderTop: `4px solid ${theme.palette.secondary.main}`,
          borderBottom: isOriginalTheme ? '1px solid #d7dfea' : 'none',
          boxShadow: 'none',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            disableGutters
            sx={{
              justifyContent: 'space-between',
              minHeight: { xs: '72px', md: '84px' },
              px: 0,
              py: { xs: 1, md: 1.5 },
            }}
          >
            {/* Logo */}
            <Logo onClick={handleLogoClick} />

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box
                sx={{
                  display: 'flex',
                  gap: { md: 1.5, lg: 2 },
                  alignItems: 'center',
                }}
              >
                {/* Text Navigation Links */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0,
                    alignItems: 'center',
                  }}
                >
                  {navigationItems.map((item) => (
                    <NavigationButton
                      key={item.label}
                      label={item.label}
                      onClick={item.onClick}
                      isActive={location.pathname.startsWith(item.href)}
                    />
                  ))}
                </Box>

                {/* Early Access CTA Button */}
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    trackCTA('header_nav', 'Early Access', {
                      destination: '/early-access',
                      from_page: location.pathname,
                    });
                    navigate('/early-access');
                  }}
                  sx={{
                    backgroundColor: isOriginalTheme
                      ? theme.palette.primary.main
                      : '#ffffff',
                    color: isOriginalTheme
                      ? '#ffffff'
                      : theme.palette.primary.main,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2.5,
                    py: 0.75,
                    minHeight: 'auto',
                    borderRadius: '9999px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    '&:hover': {
                      backgroundColor: isOriginalTheme
                        ? theme.palette.primary.dark
                        : theme.palette.grey[100],
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                    },
                  }}
                >
                  Early Access
                </Button>

                {/* Tablet: Icon Sign In + Hamburger Menu */}
                {isTablet && (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        trackCTA('header_nav', 'Sign In', {
                          destination: 'https://app.ellafin.ai',
                          from_page: location.pathname,
                          device: 'tablet',
                        });
                        window.location.href = 'https://app.ellafin.ai';
                      }}
                      sx={{
                        color: headerTextColor,
                        borderColor: theme.palette.secondary.main,
                        borderWidth: '3px',
                        textTransform: 'none',
                        fontWeight: 600,
                        minWidth: 'auto',
                        px: 1.5,
                        borderRadius: '9999px',
                        '&:hover': {
                          borderColor: theme.palette.secondary.main,
                          borderWidth: '3px',
                          backgroundColor: `${theme.palette.secondary.main}1a`,
                        },
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 18 }} />
                    </Button>
                    <MobileMenuButton onClick={() => setMobileOpen(true)} />
                  </>
                )}

                {/* Desktop: Full Sign In Button */}
                {!isTablet && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PersonIcon />}
                    onClick={() => {
                      trackCTA('header_nav', 'Sign In', {
                        destination: 'https://app.ellafin.ai',
                        from_page: location.pathname,
                        device: 'desktop',
                      });
                      window.location.href = 'https://app.ellafin.ai';
                    }}
                    sx={{
                      color: headerTextColor,
                      borderColor: theme.palette.secondary.main,
                      borderWidth: '3px',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 1.5,
                      py: 0.5,
                      minHeight: 'auto',
                      borderRadius: '9999px',
                      '&:hover': {
                        borderColor: theme.palette.secondary.main,
                        borderWidth: '3px',
                        backgroundColor: `${theme.palette.secondary.main}1a`,
                      },
                    }}
                  >
                    Sign In
                  </Button>
                )}
              </Box>
            )}

            {/* Mobile Actions (0-680px) */}
            {isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    trackCTA('header_nav', 'Sign In', {
                      destination: 'https://app.ellafin.ai',
                      from_page: location.pathname,
                      device: 'mobile',
                    });
                    window.location.href = 'https://app.ellafin.ai';
                  }}
                  sx={{
                    color: headerTextColor,
                    borderColor: theme.palette.secondary.main,
                    borderWidth: '3px',
                    textTransform: 'none',
                    fontWeight: 600,
                    minWidth: 'auto',
                    px: 1.5,
                    borderRadius: '9999px',
                    '&:hover': {
                      borderColor: theme.palette.secondary.main,
                      borderWidth: '3px',
                      backgroundColor: `${theme.palette.secondary.main}1a`,
                    },
                  }}
                >
                  <PersonIcon sx={{ fontSize: 18 }} />
                </Button>
                <MobileMenuButton onClick={() => setMobileOpen(true)} />
              </Box>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <MobileDrawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        navigationItems={[
          ...navigationItems,
          {
            label: 'Early Access',
            onClick: () => {
              trackCTA('mobile_drawer', 'Early Access', {
                destination: '/early-access',
                from_page: location.pathname,
                device: 'mobile',
              });
              navigate('/early-access');
              setMobileOpen(false);
            },
          },
          {
            label: 'Sign In',
            onClick: () => {
              trackCTA('mobile_drawer', 'Sign In', {
                destination: 'https://app.ellafin.ai',
                from_page: location.pathname,
                device: 'mobile',
              });
              window.location.href = 'https://app.ellafin.ai';
              setMobileOpen(false);
            },
          },
        ]}
        onLogoClick={handleLogoClick}
      />
    </>
  );
}

export default Header;
